import { useMemo } from 'react';
import { useGetUserByIdQuery } from '../redux/adminApiSlice';
import { Post } from '../types/interfaces';

// Format time ago helper
const formatTimeAgo = (date: Date | string): string => {
  const now = new Date();
  const past = new Date(date);
  const diffMs = now.getTime() - past.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (diffYears > 0) {
    return diffYears === 1 ? '1 year' : `${diffYears} years`;
  }
  if (diffMonths > 0) {
    return diffMonths === 1 ? '1 month' : `${diffMonths} months`;
  }
  if (diffDays > 0) {
    return diffDays === 1 ? '1 day' : `${diffDays} days`;
  }
  if (diffHours > 0) {
    return diffHours === 1 ? '1 hour' : `${diffHours} hours`;
  }
  if (diffMinutes > 0) {
    return diffMinutes === 1 ? '1 minute' : `${diffMinutes} minutes`;
  }
  return 'just now';
};

export interface UseUserDetailsOptions {
  user: Post['user'] | null;
  enabled?: boolean;
}

export interface UseUserDetailsReturn {
  displayName: string;
  username: string;
  accountAge: string | null;
  isLoading: boolean;
  userId: string | null;
}

/**
 * Hook to get user details from a post's user field
 * Handles both string (user ID) and object (partial user) cases
 */
export const useUserDetails = ({ user, enabled = true }: UseUserDetailsOptions): UseUserDetailsReturn => {
  // Extract user ID
  const userId = useMemo(() => {
    if (!user) return null;
    if (typeof user === 'string') return user;
    return user._id || null;
  }, [user]);

  // Fetch full user data - always fetch if we have a user ID, even if we have partial data
  // This ensures we get username and createdAt which aren't in the partial user object
  const shouldSkip = !userId || !enabled;
  const { data: fullUser, isLoading } = useGetUserByIdQuery(userId || '', {
    skip: shouldSkip,
  });

  // Use full user data if available (this will be a full User object with all fields)
  const userData = fullUser || null;

  // Extract display name
  const displayName = useMemo(() => {
    if (userData) {
      // Handle privacy-wrapped fields
      if (typeof userData.displayName === 'string') {
        return userData.displayName;
      }
      if (typeof userData.displayName === 'object' && userData.displayName !== null && 'content' in userData.displayName) {
        const content = (userData.displayName as { content?: string }).content;
        return content || 'Unknown User';
      }
      return 'Unknown User';
    }
    
    // Fallback to partial user data
    if (user && typeof user === 'object' && user.displayName) {
      return user.displayName;
    }
    if (user && typeof user === 'string') {
      return 'Unknown User';
    }
    return 'Unknown User';
  }, [userData, user]);

  // Extract username - only available from full user data
  const username = useMemo(() => {
    if (userData && 'username' in userData) {
      return userData.username || 'N/A';
    }
    return 'N/A';
  }, [userData]);

  // Calculate account age - only available from full user data
  const accountAge = useMemo(() => {
    if (userData && 'createdAt' in userData && userData.createdAt) {
      try {
        return formatTimeAgo(userData.createdAt);
      } catch {
        return null;
      }
    }
    return null;
  }, [userData]);

  return {
    displayName,
    username,
    accountAge,
    isLoading: isLoading && !userData,
    userId,
  };
};
